<template>
  <div class="list-row-group">
    <div
      v-if="!isCurrentlyEditingThisRow"
      class="list-row v-center space-between"
      :class="{
        clickable: rowClickable,
        'active-branch': isInActiveBranch,
        dragging: isDraggingCurrentRow,
        'invalid-drop-target': showInvalidDropIndicator
      }"
      :style="indentStyle"
      @click="hasChildren && toggleExpand()"
      @dragover="handleDragOver"
      @dragenter.prevent="handleDragEnter"
      @dragleave="handleDragLeave"
      @drop="handleDrop"
    >
      <div class="v-center flex-1 min-w-0 mr-2">
        <img
          src="~@/assets/images/icons/drag-handle.svg"
          alt="drag handle"
          class="icon-btn drag-icon-btn left-drag-icon flex-shrink-0"
          :class="{ 'action-disabled': isActionLocked,  'action-hidden': !canRearrange }"
          draggable="true"
          @click.stop
          @dragstart="handleDragStart"
          @dragend="handleDragEnd"
        />
        <div class="chevron-wrapper v-center flex-shrink-0" :class="{ 'has-children': hasChildren }">
          <img
            v-if="hasChildren"
            src="~@/assets/images/chevron-right.svg"
            class="chevron-icon"
            :class="{ 'rotate-90': shouldShowChildren }"
            @click.stop="toggleExpand"
          />
        </div>
        <span class="item-name elipsis-text" v-overflow-tooltip>{{ item.name }}</span>
        <span v-if="item.abbreviation && showAbbreviationField !== false" class="abbreviation-tag ml-2 flex-shrink-0" v-overflow-tooltip>{{ item.abbreviation }}</span>
      </div>

      <div class="action-icons v-center flex-shrink-0" :class="{ 'actions-locked': isActionLocked }" v-if="isTenantAdmin">
        <!-- <span
          v-if="showInvalidDropIndicator"
          class="drop-not-allowed-indicator"
          v-tooltip="'Can only move within the same parent scope'"
        ></span> -->
        <img v-if="canShowImportAction" src="~@/assets/images/icons/import-icon.svg" v-tooltip="'Import CSV'" @click.stop="openImportCsv" class="icon-btn" :class="{ 'action-disabled': isActionLocked }" />
        <img v-if="canShowAddAction" src="~@/assets/images/icons/plus.svg" v-tooltip="'Add Sub-item'" @click.stop="openAddChild" class="icon-btn plus-icon" :class="{ 'action-disabled opacity-50 cursor-not-allowed': isCreateLocked }" />
        <img v-if="canShowCopyAction" src="~@/assets/images/icons/copy-icon.svg" v-tooltip="'Copy Item'" @click.stop="triggerCopyItem" class="icon-btn" :class="{ 'action-disabled': isActionLocked }" />
        <template v-if="item.editable !== false && showEditAction !== false">
        <img src="~@/assets/images/pencil.svg" v-tooltip="'Edit Name'" @click.stop="startEdit" class="icon-btn" :class="{ 'action-disabled opacity-50 cursor-not-allowed': isEditLocked }" />
        <img v-if="canShowDelete && showDeleteAction !== false" src="~@/assets/images/trash-2.svg" v-tooltip="'Delete Item'" @click.stop="deleteItem" class="icon-btn" :class="{ 'action-disabled': isActionLocked }" />
        </template>
      </div>
    </div>

    <div v-else class="inline-row v-center px-3 py-1 edit-bg" :style="indentStyle">
      <div class="chevron-wrapper v-center" :class="{ 'has-children': hasChildren }">
         <img v-if="hasChildren" src="~@/assets/images/chevron-right.svg" class="chevron-icon rotate-90" style="opacity:0.3" />
      </div>
      <div class="input-container flex-grow-1 mr-3">
        <input v-model="editName" type="text" class="modern-input active-input text-xs w-100" v-focus />
      </div>
      <div v-if="showAbbreviationField !== false" class="abbreviation-container mr-3">
        <input v-model="editAbbreviation" type="text" placeholder="Abbr (optional)" class="modern-input abbr-width text-xs" />
      </div>
      <div class="btn-group-right ml-auto">
        <button class="btn text-xs mr-1" @click="saveEdit">Update</button>
        <button class="btn btn-black text-xs" @click="cancelEdit">Cancel</button>
      </div>
    </div>

    <div v-if="addingChild" class="inline-row v-center px-3 py-2 child-add-bg" :style="childIndentStyle">
      <div class="chevron-wrapper"></div>
      <div class="input-container flex-grow-1 mr-3">
        <input v-model="newName" type="text" placeholder="New item name" class="modern-input active-input text-xs w-100" v-focus />
      </div>
      <div v-if="showAbbreviationField !== false" class="abbreviation-container mr-3">
        <input v-model="newAbbreviatedName" type="text" placeholder="Abbreviation (optional)" class="modern-input abbr-width text-xs" />
      </div>
      <div class="btn-group-right ml-auto">
        <button class="btn text-xs mr-1" @click="saveNewChild">Add</button>
        <button class="btn btn-black text-xs" @click="cancelAddChild">Cancel</button>
      </div>
    </div>

    <transition name="tree-expand">
    <div v-if="shouldShowChildren" class="children-area">
      <div v-if="loading" class="px-5 py-2 text-xs italic color-grey" :style="childIndentStyle">Fetching...</div>
      <div v-else-if="visibleChildren.length === 0" class="px-5 py-2 text-xs italic no-children-text" :style="childIndentStyle">
        No sub-items found
      </div>
      <template v-else>
        <list-row
          v-for="child in visibleChildren"
          :key="child.id"
          :item="child"
          :depth="depth + 1"
          :is-tenant-admin="isTenantAdmin"
          :is-system-generated-list="isSystemGeneratedList"
          :selected-list-id="selectedListId"
          :all-values="allValues"
          :top-level-value-count="topLevelValueCount"
          :expanded-state="expandedState"
          :is-searching="isSearching"
          :selected-item-id="selectedItemId"
          :row-clickable="rowClickable"
          :read-only="readOnly"
          :fetch-children="fetchChildren"
          :save-edit-handler="saveEditHandler"
          :add-child-handler="addChildHandler"
          :always-show-chevron="alwaysShowChevron"
          :show-import-action="showImportAction"
          :show-add-action="showAddAction"
          :show-edit-action="showEditAction"
          :show-delete-action="showDeleteAction"
          :show-abbreviation-field="showAbbreviationField"
          :path-ids="currentPathIds"
          :active-path-set="activePathSet"
          :editing-row-id="editingRowId"
          :is-creating="isCreating"
          :parent-id="item.id"
          :is-rearrange-enabled="isRearrangeEnabled"
          :dragged-item-id="draggedItemId"
          :dragged-parent-id="draggedParentId"
          @refresh="$emit('refresh')"
          @edit-start="$emit('edit-start', $event)"
          @edit-end="$emit('edit-end', $event)"
          @create-start="$emit('create-start')"
          @create-end="$emit('create-end')"
          @select="$emit('select', $event)"
          @toggle-expand="$emit('toggle-expand', $event)"
          @children-loaded="$emit('children-loaded', $event)"
          @drag-start="$emit('drag-start', $event)"
          @drag-end="$emit('drag-end')"
          @copy-item="$emit('copy-item', $event)"
        />
        <div v-if="childHasMore" class="px-5 py-2 text-xs color-grey cursor-pointer" @click="loadMoreChildren()" :style="childIndentStyle">
          {{ loadingMoreChildren ? 'Loading...' : 'Load more...' }}
        </div>
      </template>
    </div>
    </transition>

    <modal v-if="!readOnly" title="Import CSV" :open="importCsvModal" @close="importCsvModal = false">
      <import-custom-list
        v-if="importCsvModal"
        :custom-list-id="selectedListId"
        :parent-id="item.id"
        @imported="handleImportComplete"
        @close="importCsvModal = false"
      />
    </modal>
    <custom-list-value-resolution-modal
      :open="childResolutionOpen"
      :title="childResolutionTitle"
      :description="childResolutionDescription"
      :source-name="childDraftName"
      :source-abbreviation="childDraftAbbreviation"
      :name-conflict="childNameConflict"
      :abbreviation-conflict="childAbbreviationConflict"
      :name-deleted-conflict="childDeletedNameConflict"
      :abbreviation-deleted-conflict="childDeletedAbbreviationConflict"
      :show-both-fields="childShowBothFields"
      :show-name-field="childShowNameField"
      :show-abbreviation-field="childShowAbbreviationField"
      :initial-name="childDraftName"
      :initial-abbreviation="childDraftAbbreviation"
      :name-error="childNameError"
      :abbreviation-error="childAbbreviationError"
      @draft-change="clearChildResolutionErrors"
      @close="closeChildResolution"
      @skip="closeChildResolution"
      @save="applyChildResolution"
    />
    <custom-list-value-resolution-modal
      :open="topLevelResolutionOpen"
      :title="topLevelResolutionTitle"
      :description="topLevelResolutionDescription"
      :source-name="topLevelDraftName"
      :source-abbreviation="topLevelDraftAbbreviation"
      :name-conflict="topLevelNameConflict"
      :name-deleted-conflict="topLevelNameDeletedConflict"
      :abbreviation-conflict="topLevelAbbreviationConflict"
      :abbreviation-deleted-conflict="topLevelAbbreviationDeletedConflict"
      :show-both-fields="topLevelShowBothFields"
      :show-name-field="topLevelShowNameField"
      :show-abbreviation-field="topLevelShowAbbreviationField"
      :initial-name="topLevelDraftName"
      :initial-abbreviation="topLevelDraftAbbreviation"
      :name-error="topLevelNameError"
      :abbreviation-error="topLevelAbbreviationError"
      @draft-change="clearTopLevelResolutionErrors"
      @close="closeTopLevelResolution"
      @skip="closeTopLevelResolution"
      @save="applyTopLevelResolution"
    />
  </div>
</template>

<script>
import {
  GetCustomListChild, CreateCustomListValues, UpdateCustomListValues, DeleteCustomListValues,
  MaterialTypeCount, GetUnitOfMaterialCount, GetTaskInStatusCount, getMaterialAttatchedCount,
  ChangeDefaultCustomValue, UpdateMaterialCustomListValue, UpdateUOMCustomValue,
  CheckChildTopLevelCustomListValueByName, CheckChildTopLevelCustomListValueByAbbreviation,
  CheckTopLevelCustomListValueByName, CheckTopLevelCustomListValueByAbbreviation
} from '@/api'
import ConfirmationDialog from '@/plugins/confirmationDialog'
import Loader from '@/plugins/loader'
import Modal from '../../common/modal.vue'
import ImportCustomList from './importCustomList.vue'
import CustomListValueResolutionModal from './customListValueResolutionModal.vue'
import { success } from '@/plugins/notification/index.js'

export default {
  name: 'ListRow',
  components: { Modal, ImportCustomList, CustomListValueResolutionModal },
  directives: { focus: { inserted: el => el.focus() } },
  props: [
    'item',
    'depth',
    'isTenantAdmin',
    'selectedListId',
    'allValues',
    'expandedState',
    'isSearching',
    'selectedItemId',
    'rowClickable',
    'readOnly',
    'fetchChildren',
    'saveEditHandler',
    'addChildHandler',
    'alwaysShowChevron',
    'showImportAction',
    'showAddAction',
    'showEditAction',
    'showDeleteAction',
    'showAbbreviationField',
    'pathIds',
    'activePathSet',
    'editingRowId',
    'isCreating',
    'isSystemGeneratedList',
    'parentId',
    'isRearrangeEnabled',
    'draggedItemId',
    'draggedParentId',
    'topLevelValueCount'
  ],
  data () {
    return {
      loading: false,
      addingChild: false,
      importCsvModal: false,
      editName: '',
      editAbbreviation: '',
      newName: '',
      newAbbreviatedName: '',
      dragHoverCounter: 0,
      childOffset: 0,
      childLimit: 30,
      childHasMore: false,
      loadingMoreChildren: false,
      childResolutionOpen: false,
      childResolutionMode: null,
      childResolutionTargetId: null,
      childResolutionParentId: null,
      childDraftName: '',
      childDraftAbbreviation: '',
      childNameConflict: false,
      childAbbreviationConflict: false,
      childDeletedNameConflict: false,
      childDeletedAbbreviationConflict: false,
      childDeletedMatch: null,
      childNameError: false,
      childAbbreviationError: false,
      childResolutionTitle: 'Resolve duplicate sub-item',
      childResolutionDescription: 'Update the name or abbreviation, or skip this item.',
      childShowBothFields: false,
      childShowNameField: true,
      childShowAbbreviationField: true,
      topLevelResolutionOpen: false,
      topLevelDraftName: '',
      topLevelDraftAbbreviation: '',
      topLevelNameConflict: false,
      topLevelNameDeletedConflict: false,
      topLevelAbbreviationConflict: false,
      topLevelAbbreviationDeletedConflict: false,
      topLevelDeletedMatch: null,
      topLevelResolutionTargetId: null,
      topLevelNameError: false,
      topLevelAbbreviationError: false,
      topLevelResolutionTitle: 'Resolve duplicate top-level item',
      topLevelResolutionDescription: 'Update the name or abbreviation, or skip this item.',
      topLevelShowBothFields: false,
      topLevelShowNameField: true,
      topLevelShowAbbreviationField: true
    }
  },
  computed: {
    indentStyle () { return { paddingLeft: `${(this.depth * 32) + 16}px` } },
    childIndentStyle () { return { paddingLeft: `${((this.depth + 1) * 32) + 16}px` } },
    hasChildren () {
      return (this.item.child_values_aggregate?.aggregate?.count || 0) > 0
    },
    isExpanded () {
      return Boolean(this.expandedState?.[this.item.id])
    },
    currentPathIds () {
      const basePathIds = Array.isArray(this.pathIds) ? this.pathIds : []
      return [...basePathIds, this.item.id]
    },
    isInActiveBranch () {
      return Boolean(this.activePathSet?.[this.item.id])
    },
    isCurrentlyEditingThisRow () {
      return this.editingRowId === this.item.id
    },
    isEditingAnyRow () {
      return this.editingRowId !== null && this.editingRowId !== undefined
    },
    isCreateLocked () {
      return this.isCreating
    },
    isEditLocked () {
      return this.isCreating || this.isActionLocked
    },
    isActionLocked () {
      return this.isCreateLocked || (this.isEditingAnyRow && !this.isCurrentlyEditingThisRow)
    },
    shouldShowChildren () {
      return this.isExpanded || (this.isSearching && Array.isArray(this.item.children) && this.item.children.length > 0)
    },
    visibleChildren () {
      const children = Array.isArray(this.item.children) ? this.item.children : []
      return children.filter(child => !child?.deleted)
    },
    normalizedAllValues () {
      return Array.isArray(this.allValues) ? this.allValues : []
    },
    topLevelParentCount () {
      if (typeof this.topLevelValueCount === 'number') return this.topLevelValueCount
      return this.normalizedAllValues.filter(value => !value.parent_id && !value.deleted).length
    },
    canShowDelete () {
      if (this.depth > 0) return true
      return this.topLevelParentCount > 1
    },
    isSystemGenerated () {
      return Boolean(this.isSystemGeneratedList)
    },
    canShowImportAction () {
      return !this.readOnly && this.showImportAction !== false && !this.isSystemGenerated
    },
    canShowAddAction () {
      return !this.readOnly && this.showAddAction !== false && !this.isSystemGenerated
    },
    canRearrange () {
      return this.isTenantAdmin && !this.readOnly && this.isRearrangeEnabled && !this.isActionLocked
    },
    canShowCopyAction () {
      return this.isTenantAdmin && !this.readOnly
    },
    isAnyItemDragging () {
      return this.draggedItemId !== null && this.draggedItemId !== undefined
    },
    isDraggingCurrentRow () {
      return this.draggedItemId === this.item.id
    },
    canDropHere () {
      if (!this.canRearrange) return false
      if (!this.isAnyItemDragging) return false
      if (this.isDraggingCurrentRow) return false
      return this.draggedParentId === this.parentId
    },
    isDragOverCurrentRow () {
      return this.dragHoverCounter > 0
    },
    showInvalidDropIndicator () {
      return this.isAnyItemDragging && this.isDragOverCurrentRow && !this.canDropHere
    }
  },
  watch: {
    draggedItemId (value) {
      if (value === null || value === undefined) {
        this.dragHoverCounter = 0
      }
    }
  },
  methods: {
    sleep (ms) {
      return new Promise(resolve => setTimeout(resolve, ms))
    },
    async fetchChildrenAfterMutation ({ createdName, minimumCount = 0, retries = 3, delayMs = 250 }) {
      const normalizedName = (createdName || '').trim().toLowerCase()
      for (let attempt = 0; attempt < retries; attempt++) {
        const res = await GetCustomListChild(this.item.id)
        const children = res.custom_list_values || []
        const hasCreatedChild = children.some(child => (child.name || '').trim().toLowerCase() === normalizedName)
        const meetsCount = children.length >= minimumCount
        if (hasCreatedChild || meetsCount || attempt === retries - 1) return children
        await this.sleep(delayMs)
      }
      return []
    },
    handleRowClick () {
      if (!this.rowClickable) return
      this.$emit('select', this.item)
    },
    beginEditing () {
      this.$emit('edit-start', this.item.id)
    },
    finishEditing () {
      this.$emit('edit-end', this.item.id)
    },
    beginCreating () {
      this.$emit('create-start')
    },
    finishCreating () {
      this.$emit('create-end')
    },
    cancelEdit () {
      this.finishEditing()
    },
    cancelAddChild () {
      this.newName = ''
      this.newAbbreviatedName = ''
      this.addingChild = false
      this.finishCreating()
    },
    triggerCopyItem () {
      if (this.isActionLocked) return
      this.$emit('copy-item', this.item)
    },
    openImportCsv () {
      if (this.isActionLocked) return
      if (this.isSystemGenerated) return
      this.importCsvModal = true
    },
    handleImportComplete () {
      this.importCsvModal = false
      this.$emit('refresh')
    },
    async toggleExpand (forceExpanded = null) {
      this.handleRowClick()
      if (!this.hasChildren && typeof this.fetchChildren !== 'function') return
      const nextExpanded = typeof forceExpanded === 'boolean' ? forceExpanded : !this.isExpanded
      this.$emit('toggle-expand', { id: this.item.id, expanded: nextExpanded, pathIds: this.currentPathIds })

      const alreadyLoaded = this.item._childrenLoaded === true
      if (nextExpanded && !alreadyLoaded && (!this.item.children || this.item.children.length === 0)) {
        this.loading = true
        try {
          await this.loadMoreChildren(true)
        } catch (err) {
          this.$notify.alert('Failed to load sub-items')
          this.$emit('toggle-expand', { id: this.item.id, expanded: false, pathIds: this.currentPathIds })
        } finally {
          this.loading = false
        }
      }
    },
    async loadMoreChildren (reset = false) {
      if (reset) {
        this.childOffset = 0
        this.childHasMore = true
        this.$set(this.item, 'children', [])
      }
      if (!this.childHasMore || this.loadingMoreChildren) return
      this.loadingMoreChildren = true
      try {
        const children = this.fetchChildren
          ? await this.fetchChildren(this.item.id, this.childOffset, this.childLimit)
          : [] // fallback handled elsewhere usually

        if (children.length < this.childLimit) {
          this.childHasMore = false
        } else {
          this.childHasMore = true
        }

        const validChildren = children.filter(c => !c.deleted)
        const newChildrenList = reset ? validChildren : [...(this.item.children || []), ...validChildren]
        this.$set(this.item, 'children', newChildrenList)
        this.$set(this.item, '_childrenLoaded', true)
        this.childOffset += this.childLimit

        this.$emit('children-loaded', { parentId: this.item.id, children: newChildrenList })
      } finally {
        this.loadingMoreChildren = false
      }
    },
    handleDragStart (event) {
      if (!this.canRearrange) return
      event.stopPropagation()
      event.dataTransfer.effectAllowed = 'move'
      event.dataTransfer.setData('text/plain', String(this.item.id))
      this.$emit('drag-start', {
        itemId: this.item.id,
        parentId: this.parentId
      })
    },
    handleDragEnd (event) {
      event.stopPropagation()
      this.dragHoverCounter = 0
      this.$emit('drag-end')
    },
    handleDragEnter (event) {
      if (!this.canRearrange || !this.isAnyItemDragging) return
      event.stopPropagation()
      this.dragHoverCounter += 1
    },
    handleDragLeave (event) {
      if (!this.canRearrange) return
      event.stopPropagation()
      this.dragHoverCounter = Math.max(0, this.dragHoverCounter - 1)
    },
    handleDragOver (event) {
      if (!this.canRearrange || !this.isAnyItemDragging) return
      event.stopPropagation()
      event.preventDefault()
      if (this.canDropHere) {
        event.dataTransfer.dropEffect = 'move'
      } else {
        event.dataTransfer.dropEffect = 'none'
      }
    },
    handleDrop (event) {
      if (!this.canRearrange) return
      event.stopPropagation()
      this.dragHoverCounter = 0
      event.preventDefault()
      if (!this.canDropHere) {
        if (this.$notify && typeof this.$notify.alert === 'function') {
          this.$notify.alert('Cannot drop here. You can only reorder within the same parent.')
        }
        this.$emit('drag-end')
        return
      }
      const sourceItemId = Number(event.dataTransfer.getData('text/plain'))
      if (!sourceItemId || sourceItemId === this.item.id) return
      this.$emit('drop-item', {
        sourceItemId,
        targetItemId: this.item.id,
        parentId: this.parentId
      })
    },
    normalizeValue (value) {
      return (value || '').trim().toLowerCase()
    },
    isTagsContext () {
      return Boolean(this.$route?.path?.includes('/settings/tags') || this.$route?.query?.tagId)
    },
    async getChildDuplicateState (name, abbreviation = '', { parentId = null, excludeId = null } = {}) {
      if (this.isTagsContext()) {
        return {
          nameConflict: false,
          abbreviationConflict: false,
          nameDeletedConflict: false,
          abbreviationDeletedConflict: false,
          deletedMatch: null,
          duplicateValues: []
        }
      }

      const trimmedName = (name || '').trim()
      const trimmedAbbreviation = (abbreviation || '').trim()
      if (!trimmedName || !this.selectedListId) {
        return {
          nameConflict: false,
          abbreviationConflict: false,
          nameDeletedConflict: false,
          abbreviationDeletedConflict: false,
          duplicateValues: []
        }
      }

      const effectiveParentId = parentId !== null && parentId !== undefined ? parentId : this.parentId
      const [nameRes, abbreviationRes] = await Promise.all([
        CheckChildTopLevelCustomListValueByName(this.selectedListId, effectiveParentId, trimmedName),
        trimmedAbbreviation
          ? CheckChildTopLevelCustomListValueByAbbreviation(this.selectedListId, effectiveParentId, trimmedAbbreviation)
          : Promise.resolve({ custom_list_values: [] })
      ])

      const nameValues = Array.isArray(nameRes?.custom_list_values) ? nameRes.custom_list_values : []
      const abbreviationValues = Array.isArray(abbreviationRes?.custom_list_values) ? abbreviationRes.custom_list_values : []
      const filteredNameValues = excludeId != null
        ? nameValues.filter(child => Number(child.id) !== Number(excludeId))
        : nameValues
      const filteredAbbreviationValues = excludeId != null
        ? abbreviationValues.filter(child => Number(child.id) !== Number(excludeId))
        : abbreviationValues
      const nameDeletedValues = filteredNameValues.filter(child => Boolean(child?.deleted))
      const abbreviationDeletedValues = filteredAbbreviationValues.filter(child => Boolean(child?.deleted))
      const activeNameValues = filteredNameValues.filter(child => !child?.deleted)
      const activeAbbreviationValues = filteredAbbreviationValues.filter(child => !child?.deleted)

      return {
        nameConflict: activeNameValues.length > 0,
        abbreviationConflict: activeAbbreviationValues.length > 0,
        nameDeletedConflict: nameDeletedValues.length > 0,
        abbreviationDeletedConflict: abbreviationDeletedValues.length > 0,
        deletedMatch: nameDeletedValues[0] || abbreviationDeletedValues[0] || null,
        duplicateValues: [...filteredNameValues, ...filteredAbbreviationValues]
      }
    },
    openChildResolution ({
      name,
      abbreviation,
      nameConflict = false,
      abbreviationConflict = false,
      nameDeletedConflict = false,
      abbreviationDeletedConflict = false,
      deletedMatch = null,
      mode = 'create',
      targetId = null,
      parentId = null
    }) {
      this.childDraftName = name || ''
      this.childDraftAbbreviation = abbreviation || ''
      this.childNameConflict = Boolean(nameConflict)
      this.childAbbreviationConflict = Boolean(abbreviationConflict)
      this.childDeletedNameConflict = Boolean(nameDeletedConflict)
      this.childDeletedAbbreviationConflict = Boolean(abbreviationDeletedConflict)
      this.childDeletedMatch = deletedMatch
      const hasActiveConflict = Boolean(nameConflict || abbreviationConflict)
      const hasDeletedConflict = Boolean(nameDeletedConflict || abbreviationDeletedConflict)
      this.childResolutionTitle = hasDeletedConflict && !hasActiveConflict
        ? 'Resolve deleted sub-item'
        : 'Resolve duplicate sub-item'
      this.childResolutionDescription = hasDeletedConflict && !hasActiveConflict
        ? 'This value already exists as deleted. You can save it as-is or skip this item.'
        : 'Update the name or abbreviation, or skip this item.'
      this.childShowBothFields = hasDeletedConflict
      this.childShowNameField = hasDeletedConflict ? true : Boolean(nameConflict)
      this.childShowAbbreviationField = hasDeletedConflict ? true : Boolean(abbreviationConflict)
      this.childResolutionMode = mode
      this.childResolutionTargetId = targetId
      this.childResolutionParentId = parentId
      this.clearChildResolutionErrors()
      this.childResolutionOpen = true
    },
    async getTopLevelDuplicateState (name, abbreviation = '', excludeId = null) {
      if (this.isTagsContext()) {
        return {
          nameConflict: false,
          nameDeletedConflict: false,
          abbreviationConflict: false,
          abbreviationDeletedConflict: false,
          deletedMatch: null
        }
      }

      const trimmedName = (name || '').trim()
      const trimmedAbbreviation = (abbreviation || '').trim()
      if (!trimmedName || !this.selectedListId) {
        return {
          nameConflict: false,
          nameDeletedConflict: false,
          abbreviationConflict: false,
          abbreviationDeletedConflict: false,
          deletedMatch: null
        }
      }

      const [nameRes, abbreviationRes] = await Promise.all([
        CheckTopLevelCustomListValueByName(this.selectedListId, trimmedName),
        trimmedAbbreviation
          ? CheckTopLevelCustomListValueByAbbreviation(this.selectedListId, trimmedAbbreviation)
          : Promise.resolve({ custom_list_values: [] })
      ])

      const nameValues = Array.isArray(nameRes?.custom_list_values) ? nameRes.custom_list_values : []
      const abbreviationValues = Array.isArray(abbreviationRes?.custom_list_values) ? abbreviationRes.custom_list_values : []
      const filteredNameValues = excludeId != null
        ? nameValues.filter(value => Number(value.id) !== Number(excludeId))
        : nameValues
      const filteredAbbreviationValues = excludeId != null
        ? abbreviationValues.filter(value => Number(value.id) !== Number(excludeId))
        : abbreviationValues
      const activeNameMatch = filteredNameValues.find(value => !value?.deleted) || null
      const deletedNameMatch = filteredNameValues.find(value => Boolean(value?.deleted)) || null
      const activeAbbreviationMatch = filteredAbbreviationValues.find(value => !value?.deleted) || null
      const deletedAbbreviationMatch = filteredAbbreviationValues.find(value => Boolean(value?.deleted)) || null

      return {
        nameConflict: Boolean(activeNameMatch),
        nameDeletedConflict: Boolean(deletedNameMatch),
        abbreviationConflict: Boolean(trimmedAbbreviation && activeAbbreviationMatch),
        abbreviationDeletedConflict: Boolean(trimmedAbbreviation && deletedAbbreviationMatch),
        deletedMatch: deletedNameMatch || deletedAbbreviationMatch
      }
    },
    openTopLevelResolution ({
      name,
      abbreviation,
      nameConflict = false,
      abbreviationConflict = false,
      nameDeletedConflict = false,
      abbreviationDeletedConflict = false,
      deletedMatch = null,
      targetId = null
    }) {
      this.topLevelDraftName = name || ''
      this.topLevelDraftAbbreviation = abbreviation || ''
      this.topLevelNameConflict = Boolean(nameConflict)
      this.topLevelNameDeletedConflict = Boolean(nameDeletedConflict)
      this.topLevelAbbreviationConflict = Boolean(abbreviationConflict)
      this.topLevelAbbreviationDeletedConflict = Boolean(abbreviationDeletedConflict)
      this.topLevelDeletedMatch = deletedMatch
      this.topLevelResolutionTargetId = targetId
      const hasActiveConflict = Boolean(nameConflict || abbreviationConflict)
      const hasDeletedConflict = Boolean(nameDeletedConflict || abbreviationDeletedConflict)
      this.topLevelResolutionTitle = hasDeletedConflict && !hasActiveConflict
        ? 'Resolve deleted top-level item'
        : 'Resolve duplicate top-level item'
      this.topLevelResolutionDescription = hasDeletedConflict && !hasActiveConflict
        ? 'This value already exists as deleted. You can save it as-is or skip this item.'
        : 'Update the name or abbreviation, or skip this item.'
      this.topLevelShowBothFields = hasDeletedConflict
      this.topLevelShowNameField = hasDeletedConflict ? true : Boolean(nameConflict)
      this.topLevelShowAbbreviationField = hasDeletedConflict ? true : Boolean(abbreviationConflict)
      this.clearTopLevelResolutionErrors()
      this.topLevelResolutionOpen = true
    },
    closeTopLevelResolution () {
      this.topLevelResolutionOpen = false
      this.topLevelDraftName = ''
      this.topLevelDraftAbbreviation = ''
      this.topLevelNameConflict = false
      this.topLevelNameDeletedConflict = false
      this.topLevelAbbreviationConflict = false
      this.topLevelAbbreviationDeletedConflict = false
      this.topLevelDeletedMatch = null
      this.topLevelResolutionTargetId = null
      this.topLevelResolutionTitle = 'Resolve duplicate top-level item'
      this.topLevelResolutionDescription = 'Update the name or abbreviation, or skip this item.'
      this.topLevelShowBothFields = false
      this.topLevelShowNameField = true
      this.topLevelShowAbbreviationField = true
      this.clearTopLevelResolutionErrors()
    },
    async validateTopLevelResolutionSave ({ name, abbreviation }) {
      const trimmedName = (name || '').trim()
      const trimmedAbbreviation = (abbreviation || '').trim()
      const initialName = this.normalizeValue(this.topLevelDraftName)
      const initialAbbreviation = this.normalizeValue(this.topLevelDraftAbbreviation)
      const nameChanged = trimmedName && this.normalizeValue(trimmedName) !== initialName
      const abbreviationChanged = trimmedAbbreviation && this.normalizeValue(trimmedAbbreviation) !== initialAbbreviation
      const hasActiveConflict = Boolean(this.topLevelNameConflict || this.topLevelAbbreviationConflict)
      const hasDeletedConflict = Boolean(this.topLevelNameDeletedConflict || this.topLevelAbbreviationDeletedConflict)

      this.clearTopLevelResolutionErrors()

      if (!hasActiveConflict && hasDeletedConflict) {
        return Boolean(trimmedName)
      }

      if (!nameChanged && !abbreviationChanged) return false

      const duplicateState = await this.getTopLevelDuplicateState(trimmedName, trimmedAbbreviation, this.topLevelResolutionTargetId)
      if (duplicateState.nameConflict || duplicateState.nameDeletedConflict) {
        this.topLevelNameError = true
      }
      if (duplicateState.abbreviationConflict || duplicateState.abbreviationDeletedConflict) {
        this.topLevelAbbreviationError = true
      }
      if (this.topLevelNameError || this.topLevelAbbreviationError) return false

      return true
    },
    async validateChildResolutionSave ({ name, abbreviation }) {
      const trimmedName = (name || '').trim()
      const trimmedAbbreviation = (abbreviation || '').trim()
      const effectiveParentId = this.childResolutionParentId !== null && this.childResolutionParentId !== undefined
        ? this.childResolutionParentId
        : this.parentId
      const excludeId = this.childResolutionMode === 'edit' ? this.childResolutionTargetId : null
      const hasActiveConflict = Boolean(this.childNameConflict || this.childAbbreviationConflict)
      const hasDeletedConflict = Boolean(this.childDeletedNameConflict || this.childDeletedAbbreviationConflict)

      this.clearChildResolutionErrors()

      if (!hasActiveConflict && hasDeletedConflict) {
        return Boolean(trimmedName)
      }

      if (trimmedName) {
        const nameRes = await CheckChildTopLevelCustomListValueByName(this.selectedListId, effectiveParentId, trimmedName)
        const nameValues = Array.isArray(nameRes?.custom_list_values) ? nameRes.custom_list_values : []
        const filteredNameValues = excludeId != null
          ? nameValues.filter(child => Number(child.id) !== Number(excludeId))
          : nameValues
        if (filteredNameValues.length) {
          this.childNameError = true
        }
      }

      if (trimmedAbbreviation) {
        const abbreviationRes = await CheckChildTopLevelCustomListValueByAbbreviation(this.selectedListId, effectiveParentId, trimmedAbbreviation)
        const abbreviationValues = Array.isArray(abbreviationRes?.custom_list_values) ? abbreviationRes.custom_list_values : []
        const filteredAbbreviationValues = excludeId != null
          ? abbreviationValues.filter(child => Number(child.id) !== Number(excludeId))
          : abbreviationValues
        if (filteredAbbreviationValues.length) {
          this.childAbbreviationError = true
        }
      }

      if (this.childNameError || this.childAbbreviationError) return false

      return true
    },
    closeChildResolution () {
      this.childResolutionOpen = false
      this.childResolutionMode = null
      this.childResolutionTargetId = null
      this.childResolutionParentId = null
      this.childDraftName = ''
      this.childDraftAbbreviation = ''
      this.childNameConflict = false
      this.childAbbreviationConflict = false
      this.childDeletedNameConflict = false
      this.childDeletedAbbreviationConflict = false
      this.childDeletedMatch = null
      this.childResolutionTitle = 'Resolve duplicate sub-item'
      this.childResolutionDescription = 'Update the name or abbreviation, or skip this item.'
      this.childShowBothFields = false
      this.childShowNameField = true
      this.childShowAbbreviationField = true
      this.clearChildResolutionErrors()
    },
    clearChildResolutionErrors () {
      this.childNameError = false
      this.childAbbreviationError = false
    },
    clearTopLevelResolutionErrors () {
      this.topLevelNameError = false
      this.topLevelAbbreviationError = false
    },
    async completeChildCreation ({ name, abbreviation, existingChildren = [] }) {
      const createdName = (name || '').trim()
      const createdAbbreviation = (abbreviation || '').trim() || null

      if (this.addChildHandler) {
        const createdChildren = await this.addChildHandler({
          item: this.item,
          name: createdName,
          abbreviation: createdAbbreviation,
          existingChildren
        })
        return Array.isArray(createdChildren) ? createdChildren : existingChildren
      }

      await CreateCustomListValues([{
        custom_list_id: this.selectedListId,
        name: createdName,
        abbreviation: createdAbbreviation,
        parent_id: this.item.id
      }])

      let children = await this.fetchChildrenAfterMutation({
        createdName,
        minimumCount: existingChildren.length + 1
      })
      if (!children.length && existingChildren.length) {
        children = existingChildren
      }
      const normalizedName = createdName.toLowerCase()
      const hasCreatedChild = children.some(child => (child.name || '').trim().toLowerCase() === normalizedName)
      if (!hasCreatedChild) {
        children = [
          ...children,
          {
            id: `temp-${Date.now()}`,
            parent_id: this.item.id,
            name: createdName,
            abbreviation: createdAbbreviation,
            deleted: false,
            editable: false,
            child_values_aggregate: { aggregate: { count: 0 } },
            _optimistic: true
          }
        ]
      }
      return children
    },
    async completeChildUpdate ({ name, abbreviation }) {
      const updatedName = (name || '').trim()
      const updatedAbbreviation = (abbreviation || '').trim() || null

      const saveRequest = this.saveEditHandler
        ? this.saveEditHandler({
          item: this.item,
          name: updatedName,
          abbreviation: updatedAbbreviation
        })
        : UpdateCustomListValues(this.item.id, {
          name: updatedName,
          abbreviation: updatedAbbreviation,
          deleted: false
        })

      await Promise.resolve(saveRequest)
    },
    async createChildValue ({ name, abbreviation, existingChildren = [] }) {
      return this.completeChildCreation({ name, abbreviation, existingChildren })
    },
    openAddChild () {
      if (this.isCreateLocked || this.isEditingAnyRow) return
      if (this.isSystemGenerated) return
      this.beginCreating()
      this.addingChild = true
      if (!this.isExpanded) {
        const count = this.item.child_values_aggregate?.aggregate?.count || 0
        if (count > 0) this.toggleExpand(true)
      }
    },
    async saveNewChild () {
      if (this.isSystemGenerated) return
      if (!this.newName.trim()) return
      const loader = new Loader(); loader.show()
      try {
        const createdName = this.newName.trim()
        const createdAbbreviation = this.newAbbreviatedName?.trim() || null
        const existingChildren = Array.isArray(this.item.children) ? [...this.item.children] : []
        const duplicateState = await this.getChildDuplicateState(createdName, createdAbbreviation || '', { parentId: this.item.id })

        if (duplicateState.nameConflict || duplicateState.abbreviationConflict || duplicateState.nameDeletedConflict || duplicateState.abbreviationDeletedConflict) {
          loader.hide()
          this.openChildResolution({
            name: createdName,
            abbreviation: createdAbbreviation || '',
            nameConflict: duplicateState.nameConflict,
            abbreviationConflict: duplicateState.abbreviationConflict,
            nameDeletedConflict: duplicateState.nameDeletedConflict,
            abbreviationDeletedConflict: duplicateState.abbreviationDeletedConflict,
            deletedMatch: duplicateState.deletedMatch,
            mode: 'create',
            parentId: this.item.id
          })
          return
        }

        const children = await this.completeChildCreation({
          name: createdName,
          abbreviation: createdAbbreviation,
          existingChildren
        })
        this.$set(this.item, 'children', children)
        this.$set(this.item, '_childrenLoaded', true)
        this.$emit('children-loaded', { parentId: this.item.id, children })
        this.$emit('toggle-expand', { id: this.item.id, expanded: true })

        const currentCount = this.item.child_values_aggregate?.aggregate?.count || 0
        if (this.item.child_values_aggregate?.aggregate) {
          this.$set(this.item.child_values_aggregate.aggregate, 'count', Math.max(currentCount + 1, children.length))
        }
        if (!this.addChildHandler) {
          this.$notify.success('Custom list value created successfully')
        }
        this.newName = ''
        this.newAbbreviatedName = ''
        this.addingChild = false
        this.finishCreating()
      } catch (err) {
        if (err?.message?.includes('Uniqueness violation') || err?.message?.includes('database query error')) {
          this.$notify.alert('Custom list value already exists')
          return
        }
        if (!err?.silent) {
          this.$notify.alert('Failed to add sub-item')
        }
      } finally {
        loader.hide()
      }
    },
    async applyChildResolution ({ name, abbreviation }) {
      const loader = new Loader(); loader.show()
      try {
        const createdName = (name || '').trim()
        const createdAbbreviation = (abbreviation || '').trim() || null
        if (!createdName) {
          loader.hide()
          return
        }

        const isSaveAllowed = await this.validateChildResolutionSave({
          name: createdName,
          abbreviation: createdAbbreviation
        })
        if (!isSaveAllowed) {
          loader.hide()
          return
        }

        if (this.childResolutionMode === 'edit') {
          await this.completeChildUpdate({
            name: createdName,
            abbreviation: createdAbbreviation
          })
          this.$emit('refresh')
          this.finishEditing()
          if (!this.saveEditHandler) {
            success('Custom list value updated successfully')
          }
          this.closeChildResolution()
          return
        }

        const existingChildren = Array.isArray(this.item.children) ? [...this.item.children] : []
        const hasActiveConflict = Boolean(this.childNameConflict || this.childAbbreviationConflict)
        let children
        if (this.childDeletedMatch && !hasActiveConflict) {
          await UpdateCustomListValues(this.childDeletedMatch.id, {
            name: createdName,
            abbreviation: createdAbbreviation,
            deleted: false
          })
          children = await this.fetchChildrenAfterMutation({
            createdName,
            minimumCount: existingChildren.length + 1
          })
        } else {
          children = await this.completeChildCreation({
            name: createdName,
            abbreviation: createdAbbreviation,
            existingChildren
          })
        }

        this.$set(this.item, 'children', children)
        this.$set(this.item, '_childrenLoaded', true)
        this.$emit('children-loaded', { parentId: this.item.id, children })
        this.$emit('toggle-expand', { id: this.item.id, expanded: true })

        const currentCount = this.item.child_values_aggregate?.aggregate?.count || 0
        if (this.item.child_values_aggregate?.aggregate) {
          this.$set(this.item.child_values_aggregate.aggregate, 'count', Math.max(currentCount + 1, children.length))
        }
        if (!this.addChildHandler) {
          this.$notify.success('Custom list value created successfully')
        }
        this.closeChildResolution()
        this.newName = ''
        this.newAbbreviatedName = ''
        this.addingChild = false
        this.finishCreating()
      } catch (err) {
        if (err?.message?.includes('Uniqueness violation') || err?.message?.includes('database query error')) {
          this.$notify.alert('Custom list value already exists')
          return
        }
        if (!err?.silent) {
          this.$notify.alert('Failed to add sub-item')
        }
      } finally {
        loader.hide()
      }
    },
    startEdit () {
      if (this.isEditLocked) return
      if (this.item.editable === false) return
      this.editName = this.item.name
      this.editAbbreviation = this.item.abbreviation
      this.beginEditing()
    },
    async saveEdit () {
      const loader = new Loader(); loader.show()
      try {
        const parentId = this.parentId
        const isChildValue = parentId !== null && parentId !== undefined

        if (isChildValue) {
          const duplicateState = await this.getChildDuplicateState(this.editName, this.editAbbreviation || '', {
            parentId,
            excludeId: this.item.id
          })
          if (duplicateState.nameConflict || duplicateState.abbreviationConflict || duplicateState.nameDeletedConflict || duplicateState.abbreviationDeletedConflict) {
            loader.hide()
            this.openChildResolution({
              name: this.editName.trim(),
              abbreviation: this.editAbbreviation || '',
              nameConflict: duplicateState.nameConflict,
              abbreviationConflict: duplicateState.abbreviationConflict,
              nameDeletedConflict: duplicateState.nameDeletedConflict,
              abbreviationDeletedConflict: duplicateState.abbreviationDeletedConflict,
              deletedMatch: duplicateState.deletedMatch,
              mode: 'edit',
              targetId: this.item.id,
              parentId
            })
            return
          }
        } else {
          const duplicateState = await this.getTopLevelDuplicateState(this.editName, this.editAbbreviation || '', this.item.id)
          if (
            duplicateState.nameConflict ||
            duplicateState.nameDeletedConflict ||
            duplicateState.abbreviationConflict ||
            duplicateState.abbreviationDeletedConflict
          ) {
            loader.hide()
            this.openTopLevelResolution({
              name: this.editName.trim(),
              abbreviation: this.editAbbreviation || '',
              nameConflict: duplicateState.nameConflict,
              nameDeletedConflict: duplicateState.nameDeletedConflict,
              abbreviationConflict: duplicateState.abbreviationConflict,
              abbreviationDeletedConflict: duplicateState.abbreviationDeletedConflict,
              deletedMatch: duplicateState.deletedMatch,
              targetId: this.item.id
            })
            return
          }
        }

        await this.completeChildUpdate({
          name: this.editName.trim(),
          abbreviation: this.editAbbreviation || null
        })
        this.$emit('refresh')
        this.finishEditing()
        if (!this.saveEditHandler) {
          success('Custom list value updated successfully')
        }
      } catch (err) {
        if (err?.message?.includes('Uniqueness violation') || err?.message?.includes('database query error')) {
          this.$notify.alert('Value already exists')
          return
        }
        if (!err?.silent) {
          this.$notify.alert('Failed to update item')
        }
      } finally {
        loader.hide()
      }
    },
    async applyTopLevelResolution ({ name, abbreviation }) {
      const loader = new Loader(); loader.show()
      try {
        const trimmedName = (name || '').trim()
        const trimmedAbbreviation = (abbreviation || '').trim() || null
        if (!trimmedName) {
          loader.hide()
          return
        }

        const isSaveAllowed = await this.validateTopLevelResolutionSave({
          name: trimmedName,
          abbreviation: trimmedAbbreviation
        })
        if (!isSaveAllowed) {
          loader.hide()
          return
        }

        await UpdateCustomListValues(this.topLevelResolutionTargetId, {
          name: trimmedName,
          abbreviation: trimmedAbbreviation,
          deleted: false
        })
        this.$emit('refresh')
        this.finishEditing()
        success('Custom list value updated successfully')
        this.closeTopLevelResolution()
      } catch (err) {
        if (err?.message?.includes('Uniqueness violation') || err?.message?.includes('database query error')) {
          this.$notify.alert('Value already exists')
          return
        }
        if (!err?.silent) {
          this.$notify.alert('Failed to update item')
        }
      } finally {
        loader.hide()
      }
    },
    deleteItem () {
      if (this.isActionLocked) return
      if (this.item.editable === false) return
      const transformedArray = this.normalizedAllValues
        .map(i => ({ value: i.id, label: i.name }))
        .filter(listItem => listItem.value !== this.item.id)
      const loader = new Loader(); loader.show()
      Promise.all([
        MaterialTypeCount(this.item.id),
        GetUnitOfMaterialCount(this.item.id),
        GetTaskInStatusCount(this.item.id),
        getMaterialAttatchedCount(this.item.id)
      ]).then(([materialTypeRes, UomRes, taskRes, materialRes]) => {
        loader.hide()
        const counts = {
          tasks: taskRes.core_tasks_aggregate.aggregate.count,
          mats: materialRes.core_material_master.length,
          uom: UomRes.core_material_master.length,
          type: materialTypeRes.core_material_master.length
        }

        if (counts.tasks || counts.mats || counts.uom || counts.type) {
          let taskMessage = counts.tasks ? `${this.item.name} is attached to ${counts.tasks} tasks.` : `${this.item.name} is in use.`
          taskMessage += ' Choose a default option to replace it.'

          ConfirmationDialog(`Are you sure you want to delete ${this.item.name}?`, (res, value) => {
            if (res && value) this.executeDelete(value, counts)
          }, undefined, undefined, undefined, taskMessage, true, transformedArray)
        } else {
          ConfirmationDialog(`Are you sure you want to delete ${this.item.name}?`, res => {
            if (res) this.executeDelete()
          })
        }
      }).catch(() => { loader.hide(); this.$notify.alert('Failed to check usage') })
    },
    executeDelete (replacementValue = null, counts = null) {
      const loader = new Loader()
      loader.show()
      DeleteCustomListValues(this.item.id).then(() => {
        if (replacementValue && counts) {
          const apiCall = counts.tasks ? ChangeDefaultCustomValue : counts.mats ? UpdateMaterialCustomListValue : UpdateUOMCustomValue
          return apiCall(this.item.id, replacementValue)
        }
      }).then(() => {
        this.$notify.success('Custom list value deleted successfully')
        this.$emit('refresh')
      }).catch((err) => {
        this.$notify.alert(err?.message ?? 'Failed to delete custom list value')
      }).finally(() => loader.hide())
    }
  }
}
</script>

<style lang="scss" scoped>
.v-center { display: flex; align-items: center; }
.list-row {
  padding: 6px 16px;
  border-bottom: 1px solid #f0f0f0;
  background: #fff;
  min-height: 38px;
  &:hover {
    background-color: #fafafa;
    .action-icons { opacity: 1; }
    .left-drag-icon { opacity: 1; }
  }
  &.active-branch {
    background: linear-gradient(
      90deg,
      rgba(233, 206, 140, 0.37) 0%,
      rgba(233, 206, 140, 0.2) 27%,
      rgba(255, 255, 255, 1) 77%
    );
    border-left: 4px solid var(--brand-color);
    padding-left: 13px;
  }
  &.selected {
    background-color: #f5f9ff;
  }
  &.clickable {
    cursor: pointer;
  }
}

.chevron-wrapper {
  width: 0;
  margin-right: 0;
  &.has-children {
    width: 24px;
    margin-right: 8px;
  }
  .chevron-icon { width: 10px; cursor: pointer; transition: transform 0.2s; &.rotate-90 { transform: rotate(90deg); } }
}
.flex-1 {
  flex: 1;
}

.min-w-0 {
  min-width: 0;
}

.flex-shrink-0 {
  flex-shrink: 0;
}

.item-name {
  font-size: var(--text-xs2); // 13px
  color: #333;
  font-weight: 500;
  min-width: 0;
}

.abbreviation-tag {
  font-size: var(--text-xs); // 10px
  background: #f4f4f4;
  padding: 1px 5px;
  border-radius: 3px;
  color: #666;
  border: 1px solid #e0e0e0;
  font-weight: 600;
  text-transform: uppercase;
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.count-badge {
  font-size: var(--text-xs1); // 10px
  background: #eef5ff;
  color: #3a86ff;
  padding: 1px 6px;
  border-radius: 4px;
  border: 1px solid #d0e3ff;
  font-weight: 600;
}
.action-icons {
  opacity: 0;
  transition: opacity 0.2s;
  img { width: 14px; height: 14px; margin-left: 8px; cursor: pointer; opacity: 0.7; &:hover { opacity: 1; } }
  .disabled-icon { width: 12px; opacity: 0.4; cursor: default; }
}

.action-disabled {
  opacity: 0.35 !important;
  cursor: not-allowed !important;
  pointer-events: none;
}

.action-hidden {
  opacity: 0 !important;
  pointer-events: none !important;
}

.left-drag-icon {
  width: 14px;
  height: 14px;
  margin-right: 8px;
  opacity: 0;
  transition: opacity 0.2s;
}

.list-row.invalid-drop-target .action-icons {
  opacity: 1;
}

.list-row.invalid-drop-target .left-drag-icon {
  opacity: 1;
}

.list-row.dragging {
  opacity: 0.55;
}

.drag-icon-btn {
  cursor: grab !important;
}

.drag-icon-btn:active {
  cursor: grabbing !important;
}

.invalid-drop-target {
  cursor: not-allowed;
}

.drop-not-allowed-indicator {
  position: relative;
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid #dc2626;
  border-radius: 50%;
  margin-left: 8px;
  margin-right: 2px;
  flex-shrink: 0;
}

.drop-not-allowed-indicator::after {
  content: '';
  position: absolute;
  width: 10px;
  height: 2px;
  background: #dc2626;
  top: 4px;
  left: 0px;
  transform: rotate(-35deg);
}

.inline-row {
  border-bottom: 1px solid #f0f0f0;
  min-height: 42px;
  &.child-add-bg { background-color: #fffcf5; border-top: 1px solid #fff3d1; border-bottom: 1px solid #fff3d1; }
  &.edit-bg { background-color: #ffffff; }
}

.modern-input {
  border: 1px solid #dee2e6;
  padding: 4px 8px;
  border-radius: 4px;
  outline: none;
  height: 30px;
  &.active-input { border: 1px solid #d4a373; box-shadow: 0 0 0 1px #d4a373; }
  &.abbr-width { width: 80px; }
}

.text-xs { font-size: var(--text-s); } // 12px
.color-grey { color: #999; }
.flex-grow-1 { flex: 1; }
.w-100 { width: 100%; }
.ml-auto { margin-left: auto; }
.no-children-text {
  color: #b0b0b0;
  font-style: italic;
  font-size: var(--text-s);
}

.tree-expand-enter-active,
.tree-expand-leave-active {
  transition: opacity 0.22s ease, transform 0.22s ease;
  transform-origin: top;
}

.tree-expand-enter,
.tree-expand-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}
</style>
